import { updatePhoneValueSearch } from '@sales-center/common'
import { FocusEvent, useCallback } from 'react'
import { useCheckIsMobileNumber } from '@user/store/utilities'
import { useFormikContext } from 'formik'
import { ILeadFormModel } from '@user/components/Leads/AddLeadForm/typings'
import { useEmailCollection } from '@user/components/EmailCollection'
import { FieldNames } from '../FieldNames'
import { useLocalisedValidations } from '@user/utils/useLocalisedValidations'
import { HereResult } from '@user/store/services/hereGeoCoder'
import { billToFieldNamePrefix } from '@user/components/Settings/EntityFormSettings/constants'

type Props = {
  fieldNames: FieldNames
  fieldNamePrefix?: string
}

export function useHandlers(props: Props) {
  const { fieldNames, fieldNamePrefix } = props

  const { values, setFieldValue } = useFormikContext<ILeadFormModel>()

  const { validatePostalCode } = useLocalisedValidations()

  const { isMobileNumber: isMobilePhone, execute: checkIsPhoneMobile } =
    useCheckIsMobileNumber(values[fieldNamePrefix + fieldNames.phone])

  const { isMobileNumber: isMobileAltPhone, execute: checkIsAltPhoneMobile } =
    useCheckIsMobileNumber(
      values[fieldNamePrefix + fieldNames.altPhone] || undefined,
    )

  const onBlurPhone = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target
      const updatedValue = updatePhoneValueSearch(value)
      if (updatedValue == '') {
        const fieldName =
          fieldNamePrefix + name == fieldNames.phone
            ? fieldNames.phoneExt
            : fieldNames.altPhoneExt
        setFieldValue(fieldNamePrefix + fieldName, '')
      }
      setFieldValue(fieldNamePrefix + name, updatedValue)
      name == 'phoneNumber' || name == 'billTo_phoneNumber'
        ? checkIsPhoneMobile(updatedValue)
        : checkIsAltPhoneMobile(updatedValue)
    },
    [
      setFieldValue,
      fieldNamePrefix,
      checkIsPhoneMobile,
      checkIsAltPhoneMobile,
      fieldNames.phone,
      fieldNames.phoneExt,
      fieldNames.altPhoneExt,
    ],
  )

  const onBlurAddress = useCallback(() => {
    const getValue = (field: string) => values[`${fieldNamePrefix}${field}`]

    const address1 = getValue(fieldNames.address1)
    const city = getValue(fieldNames.city)
    const state = getValue(fieldNames.state)
    const postalCode = getValue(fieldNames.postalCode)

    const isValidPostal = validatePostalCode(postalCode) === undefined

    if (!address1 || !city || !state || !postalCode || !isValidPostal) {
      setFieldValue(fieldNamePrefix + fieldNames.fullAddress, '')
      return
    }

    if (fieldNamePrefix !== billToFieldNamePrefix) {
      setFieldValue(
        fieldNames.fullAddress,
        `${address1} ${city} ${state} ${postalCode}`,
      )
    }
  }, [values, setFieldValue, fieldNamePrefix, fieldNames])

  const onSelectAddress = useCallback(
    (place?: HereResult) => {
      if (place?.address.label && fieldNamePrefix !== billToFieldNamePrefix) {
        setFieldValue(fieldNames.fullAddress, place?.address.label)
      }
    },
    [setFieldValue, fieldNamePrefix],
  )

  const { addEmail } = useEmailCollection<ILeadFormModel>(
    `${fieldNamePrefix}emailAddresses`,
  )

  const onAddEmailClick = useCallback(() => addEmail(), [addEmail])

  return {
    onBlurPhone,
    isMobilePhone,
    isMobileAltPhone,
    onAddEmailClick,
    onBlurAddress,
    onSelectAddress,
  }
}
